import api, { stripeApi, userApi } from '../config/interceptor';

/**
 * Every backend endpoint the dashboard calls, in one place.
 * Each function resolves to the envelope's `data` and rejects on
 * `isSuccess: false`, so pages never unwrap responses themselves.
 * Contract: docs/claude-design/DASHBOARD_PLAN_AND_DESIGN.md and the backend handoff (2026-10-28).
 */

const unwrap = (response) => {
    const body = response?.data;
    if (body?.isSuccess === false) {
        const error = new Error(body?.message?.msg || body?.error?.msg || 'Request failed');
        error.response = response;
        throw error;
    }
    return body?.data ?? body;
};

const get = (client, url, params) => client.get(url, { params }).then(unwrap);
const post = (client, url, body) => client.post(url, body).then(unwrap);
const patch = (client, url, body) => client.patch(url, body).then(unwrap);
const remove = (client, url, body) => client.delete(url, { data: body }).then(unwrap);

// Drops empty values so optional filters are never sent as "" or null.
const clean = (params = {}) => Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
);

/**
 * Normalizes a cursor-paged list response to `{ items, nextCursor }`.
 * @param {string} key - the list's field name in `data` (e.g. 'sales')
 */
export const toPage = (data, key) => ({
    items: data?.[key] ?? data?.items ?? (Array.isArray(data) ? data : []),
    nextCursor: data?.next_cursor ?? data?.nextCursor ?? null,
});

export const meService = {
    get: () => get(api, '/me'),
};

export const adminsService = {
    list: () => get(api, '/admins'),
    create: (body) => post(api, '/admins/create', body),
    promote: (body) => post(api, '/admins/promote', body),
    updateProfile: (uid, body) => patch(api, `/admins/${uid}/profile`, body),
    demote: (uid) => patch(api, `/admins/${uid}`),
    resendInvite: (email) => post(userApi, '/forgotPasswordEmail', { email }),
};

export const usersService = {
    list: (params) => get(api, '/users', clean(params)),
    get: (uid) => get(api, `/users/${uid}`),
    update: (uid, body) => patch(api, `/users/${uid}`, body),
    disable: (uid) => remove(api, `/users/${uid}`),
    restore: (uid) => post(api, `/users/${uid}/restore`),
    hardDelete: (uid) => remove(api, '/deleteUser', { target_uid: uid }),
};

export const payoutsService = {
    stripeSend: (body) => post(api, '/payout/stripe/send', body),
    stripeRetry: (body) => post(api, '/payout/stripe/retry', body),
    offlineCreate: (body) => post(api, '/payout/offline/create', body),
    setPayoutStatus: (uid, body) => post(api, `/artists/${uid}/payout-status`, body),
};

export const salesService = {
    list: (params) => get(api, '/sales', clean(params)),
    get: (id) => get(api, `/sales/${id}`),
    releaseTransfer: (id, body) => post(api, `/sales/${id}/release-transfer`, body),
};

export const subscriptionsService = {
    listRefundRequests: (params) => get(api, '/subscription/refund-requests', clean(params)),
    approveRefundRequest: (id, body) => post(api, `/subscription/refund-requests/${id}/approve`, clean(body)),
    denyRefundRequest: (id, body) => post(api, `/subscription/refund-requests/${id}/deny`, body),
    // Re-tries only the cancel after an approve returned subscription.canceled: false; never refunds again
    retryCancel: (id) => post(api, `/subscription/refund-requests/${id}/retry-cancel`),
};

// Read-only lists (handoff 02, Q7), paginated like /sales.
export const artistListsService = {
    suspended: (params) => get(api, '/artists/suspended', clean(params)),
    inDebt: (params) => get(api, '/artists/in-debt', clean(params)), // kind: outstanding_debt | negative_balance
};

export const refundRecordsService = {
    platform: (params) => get(api, '/refunds/platform', clean(params)), // kind: platform_purchase | subscription
    unmatched: (params) => get(api, '/refunds/unmatched', clean(params)),
};

export const settingsService = {
    getPayoutConfig: () => get(api, '/payout-config'),
    updatePayoutConfig: (body) => patch(api, '/payout-config', body),
    getTax: () => get(api, '/tax-settings'),
    updateTax: (body) => patch(api, '/tax-settings', body),
    getFreeTrial: () => get(api, '/free-trial-settings'),
    updateFreeTrial: (body) => patch(api, '/free-trial-settings', body),
};

export const plansService = {
    list: () => get(stripeApi, '/plans'),
    create: (body) => post(stripeApi, '/createPremiumPlan', clean(body)),
};
